import { useCallback, useEffect, useState } from "react";
import { Check, Circle, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";

const ksh = (n) => (n ? `KSh ${Math.round(n).toLocaleString()}` : "—");
const input = "mt-2 w-full border border-zinc-800 bg-black px-3 py-2 text-sm text-white outline-none focus:border-ochre";
const label = "mono block text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500";
const today = () => new Date().toLocaleDateString("en-GB", { month: "long", year: "numeric" });

const NEW = { county: "", ward: "", title: "", summary: "", facility: "", acres: "",
  build_cost_shillings: "", running_year_shillings: "", funding_route: "Not yet identified" };

/** The project desk: tick conditions, write risks, post updates, publish — without touching the API. */
export default function Projects() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState("");
  const [busy, setBusy] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(NEW);
  const [drafts, setDrafts] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/projects");
      setD(data);
    } catch (err) {
      toast.error(errText(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const call = async (key, fn, done) => {
    setBusy(key);
    try {
      await fn();
      toast.success(done);
      await load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy("");
    }
  };

  const draft = (ref, key, value) =>
    setDrafts((p) => ({ ...p, [ref]: { ...p[ref], [key]: value } }));

  if (!d) return <p className="text-zinc-600" data-testid="admin-projects-loading">Loading…</p>;

  return (
    <div data-testid="admin-projects">
      <p className="overline">Land into facilities</p>
      <h1 className="display-2 mt-4">County projects</h1>
      <p className="mt-4 max-w-3xl text-sm leading-relaxed text-zinc-400">
        Every project needs at least one risk written against it before it can be published, and eight
        conditions met before it reads as ready. Unticking a condition takes it back out of ready.
      </p>

      <button type="button" onClick={() => setCreating((v) => !v)} data-testid="admin-project-new-toggle"
        className="btn-primary mt-8 inline-flex items-center gap-2">
        <Plus size={14} /> {creating ? "Close" : "New project"}
      </button>

      {creating ? (
        <div className="mt-6 grid gap-5 border border-zinc-800 bg-[#0b0b0b] p-6 md:grid-cols-2"
          data-testid="admin-project-new">
          {[["county", "County"], ["ward", "Ward or nearest town"], ["title", "Title"],
            ["facility", "Facility type"], ["acres", "Acres"],
            ["build_cost_shillings", "Build cost, shillings"],
            ["running_year_shillings", "First running year, shillings"]].map(([k, text]) => (
            <label key={k} className="block">
              <span className={label}>{text}</span>
              <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                data-testid={`admin-project-${k.replace(/_/g, "-")}`} className={input} />
            </label>
          ))}
          <label className="block">
            <span className={label}>Funding route</span>
            <select value={form.funding_route} onChange={(e) => setForm({ ...form, funding_route: e.target.value })}
              data-testid="admin-project-funding-route" className={input}>
              {d.funding_routes.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <label className="block md:col-span-2">
            <span className={label}>Summary</span>
            <textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })}
              rows={3} data-testid="admin-project-summary" className={input} />
          </label>
          <div className="md:col-span-2">
            <button type="button" data-testid="admin-project-create" disabled={busy === "create"}
              className="btn-primary inline-flex items-center gap-2"
              onClick={() => call("create", async () => {
                await api.post("/admin/projects", {
                  ...form,
                  acres: parseFloat(form.acres) || 0,
                  build_cost_shillings: parseFloat(String(form.build_cost_shillings).replace(/[^\d.]/g, "")) || 0,
                  running_year_shillings: parseFloat(String(form.running_year_shillings).replace(/[^\d.]/g, "")) || 0,
                });
                setForm(NEW);
                setCreating(false);
              }, "Project created — write a risk, then publish")}>
              {busy === "create" ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create
            </button>
          </div>
        </div>
      ) : null}

      {!d.rows.length ? (
        <p className="mt-10 text-sm text-zinc-500" data-testid="admin-projects-empty">
          No projects yet. Create one above, or take one from the county proposals inbox.
        </p>
      ) : null}

      <div className="mt-10 space-y-px">
        {d.rows.map((r) => {
          const dr = drafts[r.reference] || {};
          return (
            <div key={r.reference} className="border border-zinc-900 bg-[#0b0b0b]"
              data-testid={`admin-project-${r.reference}`}>
              <button type="button" onClick={() => setOpen(open === r.reference ? "" : r.reference)}
                data-testid={`admin-project-open-${r.reference}`}
                className="flex w-full flex-wrap items-center justify-between gap-4 p-5 text-left hover:bg-[#141414]">
                <span>
                  <span className="mono block text-[0.55rem] uppercase tracking-[0.16em] text-ochre">
                    {r.stage_label} · {r.readiness.met}/{r.readiness.total} · {r.published ? "Published" : "Draft"}
                  </span>
                  <span className="mt-2 block font-display text-base uppercase tracking-tight text-white">
                    {r.title}
                  </span>
                  <span className="mono mt-1 block text-[0.55rem] uppercase tracking-[0.14em] text-zinc-500">
                    {r.county}{r.ward ? ` · ${r.ward}` : ""} · {r.reference} · build {ksh(r.build_cost_shillings)}
                  </span>
                </span>
                <span className="mono text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">
                  {open === r.reference ? "Close" : "Open"}
                </span>
              </button>

              {open === r.reference ? (
                <div className="border-t border-zinc-900 p-5">
                  <p className={label}>Conditions</p>
                  <div className="mt-3 grid gap-2 md:grid-cols-2">
                    {r.conditions.map((c) => (
                      <button key={c.id} type="button" disabled={busy === `${r.reference}-${c.id}`}
                        data-testid={`admin-condition-${r.reference}-${c.id}`}
                        onClick={() => call(`${r.reference}-${c.id}`, () => api.post(
                          `/admin/projects/${r.reference}/condition`,
                          { condition: c.id, met: !c.met, dated: c.met ? "" : today(), note: c.note || "" }),
                          c.met ? "Condition unticked" : "Condition ticked")}
                        className="flex items-start gap-3 border border-zinc-800 p-3 text-left hover:border-ochre">
                        {c.met ? <Check size={14} className="mt-[2px] shrink-0 text-ochre" />
                          : <Circle size={12} className="mt-[4px] shrink-0 text-zinc-600" />}
                        <span className={`text-xs ${c.met ? "text-white" : "text-zinc-400"}`}>
                          {c.label}{c.met && c.dated ? ` · ${c.dated}` : ""}
                        </span>
                      </button>
                    ))}
                  </div>

                  <div className="mt-6 grid gap-6 md:grid-cols-2">
                    <div>
                      <p className={label}>Post an update</p>
                      <input value={dr.dated ?? today()} onChange={(e) => draft(r.reference, "dated", e.target.value)}
                        data-testid={`admin-update-dated-${r.reference}`} className={input} />
                      <textarea value={dr.body || ""} rows={3} placeholder="What moved, or what stopped moving"
                        onChange={(e) => draft(r.reference, "body", e.target.value)}
                        data-testid={`admin-update-body-${r.reference}`} className={input} />
                      <button type="button" disabled={busy === `${r.reference}-update`}
                        data-testid={`admin-update-post-${r.reference}`}
                        onClick={() => call(`${r.reference}-update`, async () => {
                          await api.post(`/admin/projects/${r.reference}/update`,
                            { dated: dr.dated || today(), body: dr.body || "" });
                          draft(r.reference, "body", "");
                        }, "Update posted")}
                        className="btn-ghost mt-3 inline-flex items-center gap-2">Post update</button>
                    </div>

                    <div>
                      <p className={label}>Write a risk</p>
                      <input value={dr.risk || ""} placeholder="What could stop this"
                        onChange={(e) => draft(r.reference, "risk", e.target.value)}
                        data-testid={`admin-risk-${r.reference}`} className={input} />
                      <input value={dr.mitigation || ""} placeholder="What would reduce it"
                        onChange={(e) => draft(r.reference, "mitigation", e.target.value)}
                        data-testid={`admin-risk-mitigation-${r.reference}`} className={input} />
                      <button type="button" disabled={busy === `${r.reference}-risk`}
                        data-testid={`admin-risk-add-${r.reference}`}
                        onClick={() => call(`${r.reference}-risk`, async () => {
                          await api.post(`/admin/projects/${r.reference}/risk`,
                            { risk: dr.risk || "", mitigation: dr.mitigation || "" });
                          draft(r.reference, "risk", "");
                          draft(r.reference, "mitigation", "");
                        }, "Risk recorded")}
                        className="btn-ghost mt-3 inline-flex items-center gap-2">Add risk</button>
                      <p className="mono mt-3 text-[0.55rem] uppercase tracking-[0.14em] text-zinc-600">
                        {(r.risks || []).length} recorded
                      </p>
                    </div>

                    <div>
                      <p className={label}>Record a contribution in kind</p>
                      <select value={dr.kind || d.contribution_kinds[0]} data-testid={`admin-contrib-kind-${r.reference}`}
                        onChange={(e) => draft(r.reference, "kind", e.target.value)} className={input}>
                        {d.contribution_kinds.map((k) => <option key={k} value={k}>{k}</option>)}
                      </select>
                      <input value={dr.detail || ""} placeholder="What was given"
                        onChange={(e) => draft(r.reference, "detail", e.target.value)}
                        data-testid={`admin-contrib-detail-${r.reference}`} className={input} />
                      <input value={dr.credit || "Anonymous"} placeholder="Credit, or Anonymous"
                        onChange={(e) => draft(r.reference, "credit", e.target.value)}
                        data-testid={`admin-contrib-credit-${r.reference}`} className={input} />
                      <button type="button" disabled={busy === `${r.reference}-contrib`}
                        data-testid={`admin-contrib-add-${r.reference}`}
                        onClick={() => call(`${r.reference}-contrib`, async () => {
                          await api.post(`/admin/projects/${r.reference}/contribution`, {
                            kind: dr.kind || d.contribution_kinds[0], detail: dr.detail || "",
                            credit: dr.credit || "Anonymous", dated: today(),
                          });
                          draft(r.reference, "detail", "");
                        }, "Contribution recorded")}
                        className="btn-ghost mt-3 inline-flex items-center gap-2">Add contribution</button>
                    </div>

                    <div>
                      <p className={label}>Stage</p>
                      <select value={dr.stage || r.stage} data-testid={`admin-stage-${r.reference}`}
                        onChange={(e) => draft(r.reference, "stage", e.target.value)} className={input}>
                        {d.stages.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                      </select>
                      <input value={dr.why || r.stage_why || ""} placeholder="Why, if it stalled or was abandoned"
                        onChange={(e) => draft(r.reference, "why", e.target.value)}
                        data-testid={`admin-stage-why-${r.reference}`} className={input} />
                      <button type="button" disabled={busy === `${r.reference}-stage`}
                        data-testid={`admin-stage-save-${r.reference}`}
                        onClick={() => call(`${r.reference}-stage`, () => api.post(
                          `/admin/projects/${r.reference}/stage`,
                          { stage: dr.stage || r.stage, why: dr.why ?? r.stage_why ?? "" }), "Stage saved")}
                        className="btn-ghost mt-3 inline-flex items-center gap-2">Save stage</button>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-wrap items-center gap-4 border-t border-zinc-900 pt-5">
                    {r.published ? (
                      <button type="button" data-testid={`admin-unpublish-${r.reference}`}
                        onClick={() => call(`${r.reference}-pub`, () =>
                          api.post(`/admin/projects/${r.reference}/unpublish`), "Taken off the site")}
                        className="btn-ghost">Unpublish</button>
                    ) : (
                      <button type="button" data-testid={`admin-publish-${r.reference}`}
                        onClick={() => call(`${r.reference}-pub`, () =>
                          api.post(`/admin/projects/${r.reference}/publish`), "Published")}
                        className="btn-primary">Publish</button>
                    )}
                    <a href={`/projects/${r.reference}`} target="_blank" rel="noreferrer"
                      data-testid={`admin-view-${r.reference}`}
                      className="mono text-[0.55rem] uppercase tracking-[0.16em] text-ochre hover:text-white">
                      View the public page
                    </a>
                    <button type="button" data-testid={`admin-delete-${r.reference}`}
                      onClick={() => call(`${r.reference}-del`, () =>
                        api.delete(`/admin/projects/${r.reference}`), "Deleted")}
                      className="mono ml-auto inline-flex items-center gap-2 text-[0.55rem] uppercase tracking-[0.16em] text-maasai hover:text-white">
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
